import { createAsyncThunk } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import i18next from 'i18next';
import { omit } from 'lodash';
import {
  fetchCriaDiagnostico,
  generateReportDiagnosticoDI,
  generateReportRegistroDI,
  fetchTransmissaoDuimp,
  fetchRegistraDuimp,
  fetchTransmissaoDiagnosticoDuimpById,
  fetchPlanilhaTratamentoTributarioDuimp,
  fetchPlanilhaFundamentoLegalDuimp,
  importAtributosTratamentosTributariosDuimp,
  importAtributosFundamentosLegaisDuimp,
  fetchObterChavesRegistroDuimp,
  fetchRetificarDuimp,
  fetchApagarTransmissaoDuimp,
} from '../../ts/common/services/declaracaoImportacaoApi';
import { DECLARACAO_IMPORTACAO_ENDPOINT } from '../../utils/api/domain/domain-endpoint-utils';
import { DEFAULT_TIPO_DI_DUIMP_PARAMETER } from '../../utils/general/fatura/faturaUtils';
import { finishLoading, startLoading } from '../application-state/applicationStateSlice';
import { faturaAPI } from '../fatura/faturaAPI';
import { setErrorFeedback, setSuccessFeedback } from '../feedback/feedbackSlice';
import { followUpAPI } from '../follow-up/followUpAPI';
import {
  calcularDisLote,
  gerarXMLDiagnosticoLote,
  releaseDisEmLote,
  retransmitirDisEmLote,
  liberarDisEmLote,
  declaracaoImportacaoAPI,
} from './declaracaoImportacaoAPI';
import { MediaType } from '../../utils/api/api-utils';
import declaracaoImportacaoStorage from './declaracaoImportacaoStorage';
import { integrarImportacao } from '../e-mix/EmixApi';

const fetchAllFromFaturaAsync = createAsyncThunk('declaracaoImportacao/fetchAllFromFatura', async () => {
  const { data } = await faturaAPI.fetchByFilter([DEFAULT_TIPO_DI_DUIMP_PARAMETER]);
  return { data };
});

const fetchAllDecImportWithPaginationAsync = createAsyncThunk(
  'declaracaoImportacao/fetchAllFromFaturaPaginated',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await declaracaoImportacaoAPI.fetchByFilterPaginate([
        DEFAULT_TIPO_DI_DUIMP_PARAMETER,
        ...params,
      ]);
      return { data };
    } catch (error) {
      console.log('error', error);
      return rejectWithValue(error.response.data);
    }
  }
);

const generateReportDIDiagnosticoAsync = createAsyncThunk(
  'declaracaoImportacao/generateReportDIDiagnostics',
  async ({ id, processo }, { rejectWithValue, dispatch }) => {
    try {
      const response = await generateReportDiagnosticoDI(id);

      const blob = new Blob([response.data], { type: 'application/xml;charset=utf-8' });

      saveAs(blob, `DI - ${processo ?? ''} - Diagnóstico.xml`);

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const generateReportDIRegistroAsync = createAsyncThunk(
  'declaracaoImportacao/generateReportDIRegistro',
  async ({ id, processo }, { rejectWithValue, dispatch }) => {
    try {
      const response = await generateReportRegistroDI(id);

      const blob = new Blob([response.data], { type: 'application/xml;charset=utf-8' });

      saveAs(blob, `DI - ${processo ?? ''} - Registro.xml`);

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const fetchByIdFromFaturaAsync = createAsyncThunk(
  'declaracaoImportacao/fetchByIdFromFatura',
  async (data, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await faturaAPI.fetchById(data);
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }

    let followUpResponse;
    if (response.status === 200) {
      try {
        followUpResponse = await followUpAPI.fetchById(response?.data?.followUp?.id);
      } catch (error) {
        dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
        return rejectWithValue(error?.response?.data);
      }
    }

    return {
      response: {
        status: response.status,
        data: { ...response.data, followUp: followUpResponse?.data ?? response.data.followUp },
      },
    };
  }
);

const fetchCapaDiDuimpByIdFromFaturaAsync = createAsyncThunk(
  'declaracaoImportacao/fetchCapaDiDuimpByIdFromFaturaAsync',
  async (data, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await faturaAPI.fetchCapaDiDuimpById(data);
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }

    let followUpResponse;
    if (response.status === 200) {
      try {
        followUpResponse = await followUpAPI.fetchById(response?.data?.followUp?.id);
      } catch (error) {
        dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
        return rejectWithValue(error?.response?.data);
      }
    }

    return {
      response: {
        status: response.status,
        data: { ...response.data, followUp: followUpResponse?.data ?? response.data.followUp },
      },
    };
  }
);

const conferirByIdFromFaturaAsync = createAsyncThunk(
  'declaracaoImportacao/conferirByIdFromFatura',
  async ({ id, onConflictCallback, ignoreAliquotas, refetch }, { dispatch, rejectWithValue }) => {
    try {
      let response;

      if (ignoreAliquotas) {
        response = await faturaAPI.conferirIgnoreAliquotasBy(id);
      } else {
        response = await faturaAPI.conferirBy(id);
      }

      if (refetch) {
        dispatch(fetchCapaDiDuimpByIdFromFaturaAsync(id));
      }

      if (response?.status === 200) {
        integrarImportacao(id);
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      // não deve ser a melhor forma de fazer, mas foi o que consegui pensar no momento
      // se tiver conflito com as aliquotas, chama a callback definida no componente
      if (error?.response?.status === 409 && onConflictCallback) {
        onConflictCallback({
          args: { id },
          onOpenCallback: () => ({ options: { message: error?.response?.data?.message } }),
        });
      }

      return rejectWithValue(error?.response?.data);
    }
  }
);

const duplicarByIdFromFaturaAsync = createAsyncThunk(
  'declaracaoImportacao/duplicarById',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await faturaAPI.duplicarBy(data?.id, [DECLARACAO_IMPORTACAO_ENDPOINT], data?.payload);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: 'Duplicada com sucesso!' }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const liberarByIdFromFaturaAsync = createAsyncThunk(
  'declaracaoImportacao/liberarByIdFromFatura',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await faturaAPI.liberarBy(data?.id);

      if (data?.refetch) {
        dispatch(fetchCapaDiDuimpByIdFromFaturaAsync(data?.id));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const retransmitirByIdFromFaturaAsync = createAsyncThunk(
  'declaracaoImportacao/retransmitirByIdFromFatura',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await faturaAPI.retransmitirBy(data?.id);

      if (data?.refetch) {
        dispatch(fetchCapaDiDuimpByIdFromFaturaAsync(data?.id));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const activateByIdFromFaturaAsync = createAsyncThunk('declaracaoImportacao/activateByIdFromFatura', async (id) => {
  const { status, data } = await faturaAPI.activateById(id);
  return { response: { status, data } };
});

const inactivateByIdFromFaturaAsync = createAsyncThunk('declaracaoImportacao/inactivateByIdFromFatura', async (id) => {
  const { status, data } = await faturaAPI.inactivateById(id);
  return { response: { status, data } };
});

const reabrirByIdAsync = createAsyncThunk(
  'declaracaoUnicaExportacao/reabrirById',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());

      const response = await faturaAPI.reabrirById(data?.id);

      if (response?.status === 200) {
        dispatch(finishLoading());
        dispatch(fetchAllFromFaturaAsync());
      }

      if (data?.refetch) {
        dispatch(fetchCapaDiDuimpByIdFromFaturaAsync(data?.id));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const diagnosticoDiByIdAsync = createAsyncThunk(
  'declaracaoImportacao/diagnosticoDiById',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchCriaDiagnostico(
        data.declaracaoImportacao.id,
        data.declaracaoImportacao.tipo,
        data.declaracaoImportacao?.transmitirComImpedimentos
      );

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: response?.data }));
      }

      if (response?.status === 500 || response?.status === 404 || response?.status === 401) {
        const message = response?.data ? response?.data : 'Erro não esperado!';

        dispatch(setErrorFeedback({ message }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));

      return rejectWithValue(error?.response?.data);
    }
  }
);

const transmissaoDuimpByIdAsync = createAsyncThunk(
  'declaracaoImportacao/transmissaoDuimpById',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchTransmissaoDuimp(
        data.declaracaoImportacao.id,
        data.declaracaoImportacao.tipo,
        data.declaracaoImportacao.transmitirComErros,
        data.despachante
      );

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: response?.data }));
      }

      if (response?.status === 500 || response?.status === 404 || response?.status === 401) {
        const message = response?.data ? response?.data : 'Erro não esperado!';

        dispatch(setErrorFeedback({ message }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));

      return rejectWithValue(error?.response?.data);
    }
  }
);

const apagarTransmissaoDuimpAsync = createAsyncThunk(
  'declaracaoImportacao/apagarTransmissaoDuimp',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchApagarTransmissaoDuimp(data.declaracaoImportacao.id);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: response?.data }));
      }

      if (response?.status === 500 || response?.status === 404 || response?.status === 401) {
        const message = response?.data ? response?.data : 'Erro não esperado!';

        dispatch(setErrorFeedback({ message }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));

      return rejectWithValue(error?.response?.data);
    }
  }
);

const transmissaoDiagnosticoDuimpByIdAsync = createAsyncThunk(
  'declaracaoImportacao/transmissaoDiagnosticoDuimpByIdAsync',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchTransmissaoDiagnosticoDuimpById(
        data.declaracaoImportacao.id,
        data.declaracaoImportacao.tipo,
        true
      );

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: response?.data }));
      }

      if (response?.status === 500 || response?.status === 404 || response?.status === 401) {
        const message = response?.data ? response?.data : 'Erro não esperado!';

        dispatch(setErrorFeedback({ message }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));

      return rejectWithValue(error?.response?.data);
    }
  }
);

const registrarDuimpByIdAsync = createAsyncThunk(
  'declaracaoImportacao/registrarDuimpByIdAsync',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchRegistraDuimp(
        data.declaracaoImportacao.id,
        data.declaracaoImportacao.tipo,
        data.declaracaoImportacao.transmitirComErros
      );

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: response?.data }));
      }

      if (response?.status === 500 || response?.status === 404 || response?.status === 401) {
        const message = response?.data ? response?.data : 'Erro não esperado!';

        dispatch(setErrorFeedback({ message }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));

      return rejectWithValue(error?.response?.data);
    }
  }
);

const obterChavesDuimpAsync = createAsyncThunk(
  'declaracaoImportacao/obterChavesDuimpAsync',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchObterChavesRegistroDuimp(data.declaracaoImportacao.id);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: response?.data }));
      }

      if (response?.status === 500 || response?.status === 404 || response?.status === 401) {
        const message = response?.data ? response?.data : 'Erro não esperado!';

        dispatch(setErrorFeedback({ message }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));

      return rejectWithValue(error?.response?.data);
    }
  }
);

const retificarDuimpAsync = createAsyncThunk(
  'declaracaoImportacao/retificarDuimpAsync',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchRetificarDuimp(data.declaracaoImportacao.id);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: response?.data }));
      }

      if (response?.status === 500 || response?.status === 404 || response?.status === 401) {
        const message = response?.data ? response?.data : 'Erro não esperado!';

        dispatch(setErrorFeedback({ message }));
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));

      return rejectWithValue(error?.response?.data);
    }
  }
);

const calcularDisLoteAsync = createAsyncThunk(
  'declaracaoImportacao/calcularDisLote',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data, successCallback, errorCallback } = payload;

      const response = await calcularDisLote(data);

      if (response?.status === 200) {
        const { success = [], errors = [] } = response.data; // Default to empty arrays if undefined
        let combinedMessage = '';

        if (success.length) {
          combinedMessage += '                  SUCESSOS: \n';
          for (let i = 0; i < success.length; i += 1) {
            combinedMessage += `${success[i]}\n`;
          }
        }
        if (errors.length) {
          combinedMessage += '                   ERROS: \n';
          for (let i = 0; i < errors.length; i += 1) {
            combinedMessage += `${errors[i]}\n`;
          }
        }
        if (errors.length > 0) {
          dispatch(setErrorFeedback({ message: combinedMessage }));
          errorCallback();
        } else {
          dispatch(setSuccessFeedback({ message: combinedMessage }));
          successCallback();
        }
      } else {
        errorCallback();
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message || 'An unknown error occurred' }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const releaseDisEmLoteAsync = createAsyncThunk(
  'declaracaoImportacao/releaseDisEmLote',
  async ({ data, successCallback }, { rejectWithValue, dispatch }) => {
    try {
      const response = await releaseDisEmLote(data);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: 'Conferido com sucesso!' }));

        if (typeof successCallback === 'function') {
          successCallback();
        }
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data.message }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const retransmitirDisEmLoteAsync = createAsyncThunk(
  'declaracaoImportacao/retransmitirDisEmLote',
  async ({ data, successCallback }, { rejectWithValue, dispatch }) => {
    try {
      const response = await retransmitirDisEmLote(data);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: 'Retransmitido com sucesso!' }));

        if (typeof successCallback === 'function') {
          successCallback();
        }
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data.message }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const liberarDisEmLoteAsync = createAsyncThunk(
  'declaracaoImportacao/liberarDisEmLote',
  async ({ data, successCallback }, { rejectWithValue, dispatch }) => {
    try {
      const response = await liberarDisEmLote(data);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: 'Liberado com sucesso!' }));

        if (typeof successCallback === 'function') {
          successCallback();
        }
      }

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback('Erro ao liberar!'));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const gerarXMLDIagnosticoLoteAsync = createAsyncThunk(
  'declaracaoImportacao/gerarXMLDiagnosticoLote',
  async ({ data, successCallback }, { rejectWithValue, dispatch }) => {
    try {
      const { payload, tipo } = data;

      const response = await gerarXMLDiagnosticoLote(payload, tipo);

      if (response?.status === 200) {
        dispatch(setSuccessFeedback({ message: 'XMLs gerados com sucesso! Aguarde o download do arquivo' }));

        if (typeof successCallback === 'function') {
          successCallback();
        }
      }
      saveAs(new Blob([response.data], { type: 'application/xml;charset=utf-8' }), `relatorios-${tipo}.xml`);

      return {
        response: {
          status: response?.status,
          data: response?.data,
          ...omit(response, ['request', 'config']),
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const fetchMercadoriasByFaturaIdAsync = createAsyncThunk(
  'declaracaoImportacao/fetchMercadoriasByFaturaIdAsync',
  async (id, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await faturaAPI.fetchMercadoriasByFaturaId(id);
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const fetchAdicoesByDiDuimpIdAsync = createAsyncThunk(
  'declaracaoImportacao/fetchAdicoesByDiDuimpIdAsync',
  async (id, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await declaracaoImportacaoAPI.fetchAdicoesByDiDuimpId(id);
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const fetchAdicoesPaginacaoMetadataByDiDuimpIdAsync = createAsyncThunk(
  'declaracaoImportacao/fetchAdicoesPaginacaoMetadataByDiDuimpIdAsync',
  async (id, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await declaracaoImportacaoAPI.fetchAdicoesPaginacaoMetadataByDiDuimpId(id);
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const updateMercadoriaAsync = createAsyncThunk(
  'declaracaoImportacao/updateMercadoriaAsync',
  async (data, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await declaracaoImportacaoAPI.updateMercadoria(data.idFatura, data);

      if (data?.novoItem) {
        const botaoNovo = document.querySelector('#add-item-button');

        setTimeout(() => {
          botaoNovo?.click?.();
        }, 500);
      }
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const updateAdicaoAsync = createAsyncThunk(
  'declaracaoImportacao/updateAdicaoAsync',
  async (data, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await declaracaoImportacaoAPI.updateAdicao(data);

      if (response.status === 200) {
        dispatch(
          setSuccessFeedback({
            message: i18next.t('com.muralis.qcx.mensagem.adicaoDeclaracaoImportacaoSalva', {
              adicaoNumber: response.data?.item,
            }),
          })
        );
      }
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message ?? 'Erro ao atualizar adição' }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const obterPlanilhaFundamentoLegalDuimp = createAsyncThunk(
  'declaracaoImportacao/obterPlanilhaFundamentoLegalDuimp',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchPlanilhaFundamentoLegalDuimp(id);
      console.log(response.data);

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: MediaType.EXCEL });
        saveAs(blob, 'atributos-fundamento-legal.xlsx');
      }

      return response.data;
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const obterPlanilhaTratamentoTributarioDuimp = createAsyncThunk(
  'declaracaoImportacao/obterPlanilhaTratamentoTributarioDuimp',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchPlanilhaTratamentoTributarioDuimp(id);
      console.log('teste 4');
      if (response.status === 200) {
        // const blob = new Blob([response.data], { type: 'application/xml;charset=utf-8' });
        // saveAs(blob, 'atributos_tratamentos.xlsx');

        const file = new File([response.data], 'atributos_tratamentos.xlsx', {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const url = window.URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
      }

      return response.data;
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));
      return rejectWithValue(error?.response?.data);
    }
  }
);

const importarTratamentosTributariosThunk = createAsyncThunk(
  'declaracaoImportacao/importarTratamentosTributariosThunk',
  async (data, { rejectWithValue, dispatch }) => {
    let response;
    try {
      response = await importAtributosTratamentosTributariosDuimp(data);

      if (response.status === 200) {
        dispatch(
          setSuccessFeedback({
            message: 'Importacao realizada com sucesso !!',
          })
        );
      }
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const callSyncAdicoesAPIAsync = createAsyncThunk(
  'declaracaoImportacao/callSyncAdicoesAPIAsync',
  async ({ faturaId, storedAdicoes, clientHashesPayload }, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await declaracaoImportacaoAPI.syncAdicoes(faturaId, clientHashesPayload);
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        faturaId,
        storedAdicoes,
        status: response.status,
        data: response.data,
      },
    };
  }
);

const syncAdicoesAsync = createAsyncThunk(
  'declaracaoImportacao/syncAdicoesAsync',
  async ({ faturaId }, { dispatch, rejectWithValue }) => {
    let storedAdicoes = [];

    try {
      storedAdicoes = (await declaracaoImportacaoStorage.readAdicoesFromStorage(faturaId)) ?? [];
      const adicoesHash = {};

      storedAdicoes.forEach((a) => {
        const mercadoriasHash = {};

        a.mercadorias.forEach((m) => {
          mercadoriasHash[m.id] = m.version;
        });

        adicoesHash[a.id] = {
          version: a.version,
          mercadoriasHash,
        };
      });

      dispatch(callSyncAdicoesAPIAsync({ faturaId, storedAdicoes, clientHashesPayload: { adicoesHash } }));
    } catch (err) {
      // silent catch
    }

    return { storedAdicoes };
  }
);

const obterPlanilhaAtributosItensDuimpAsync = createAsyncThunk(
  'declaracaoImportacao/obterPlanilhaAtributosItensDuimpAsync',
  async (id, { dispatch, rejectWithValue }) => {
    let response;
    try {
      response = await declaracaoImportacaoAPI.fetchPlanilhaAtributosItensDuimpAsync(id);
      if (response.status === 200) {
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'atributos_itens_duimp.xlsx';

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          console.log(match);
          if (match) {
            const [_, newFileName, ...rest] = match;
            if (newFileName) fileName = newFileName;
          }
        }
        const file = new File([response.data], fileName, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        dispatch(
          setSuccessFeedback({
            message: 'Planilha gerada com sucesso !!',
          })
        );
      }
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message ?? 'Erro ao atualizar adição' }));
      return rejectWithValue(error?.response?.data);
    }
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const importarAtributosItensDuimpAsync = createAsyncThunk(
  'declaracaoImportacao/importarAtributosItensDuimpAsync',
  async (data, { dispatch, rejectWithValue }) => {
    let response;
    try {
      response = await declaracaoImportacaoAPI.fetchImportarAtributosItensDuimpAsync(data);
      if (response.status === 200) {
        dispatch(
          setSuccessFeedback({
            message: 'Importação realizada com sucesso !!',
          })
        );
      }
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message ?? 'Erro ao atualizar adição' }));
      return rejectWithValue(error?.response?.data);
    }
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const importarFundamentosLegaisThunk = createAsyncThunk(
  'declaracaoImportacao/importarFundamentosLegaisThunk',
  async (data, { rejectWithValue, dispatch }) => {
    let response;
    try {
      response = await importAtributosFundamentosLegaisDuimp(data);

      if (response.status === 200) {
        dispatch(
          setSuccessFeedback({
            message: 'Importacao realizada com sucesso !!',
          })
        );
      }
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const atualizaDadosCargaAsync = createAsyncThunk(
  'declaracaoImportacao/atualizaDadosCargaAsync',
  async (faturaId, { rejectWithValue, dispatch }) => {
    let response;

    try {
      response = await declaracaoImportacaoAPI.atualizaDadosCarga(faturaId);

      if (response?.status < 300) {
        dispatch(setSuccessFeedback({ message: 'Dados atualizados com sucesso!' }));
      }

      return {
        response: {
          status: response.status,
          data: response.data,
        },
      };
    } catch (error) {
      dispatch(
        setErrorFeedback({
          message: error?.response?.data ?? error?.response?.data?.message ?? 'Erro ao consultar dados de carga',
        })
      );
      return rejectWithValue(error.response.data);
    }
  }
);

const fetchPaginatedMercadoriasAsync = createAsyncThunk(
  'declaracaoImportacao/fetchPaginatedMercadorias',
  async ({ faturaDiDuimpId, page, size, search }, { dispatch, rejectWithValue }) => {
    try {
      const response = await declaracaoImportacaoAPI.fetchMercadoriasPaginated(faturaDiDuimpId, [
        { name: 'page', value: page },
        { name: 'size', value: size },
        { name: 'search', value: search },
      ]);

      return {
        response: {
          status: response.status,
          data: response.data,
        },
      };
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data }));
      return rejectWithValue(error.response.data);
    }
  }
);

const fetchMercadoriaByIdAsync = createAsyncThunk(
  'declaracaoImportacao/fetchMercadoriaByIdAsync',
  async (id, { dispatch, rejectWithValue }) => {
    let response;

    try {
      response = await declaracaoImportacaoAPI.fetchMercadoriaById(id);
    } catch (error) {
      dispatch(setErrorFeedback({ message: error?.response?.data?.message }));
      return rejectWithValue(error?.response?.data);
    }

    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const declaracaoImportacaoThunks = {
  fetchAllFromFaturaAsync,
  fetchByIdFromFaturaAsync,
  conferirByIdFromFaturaAsync,
  duplicarByIdFromFaturaAsync,
  activateByIdFromFaturaAsync,
  inactivateByIdFromFaturaAsync,
  liberarByIdFromFaturaAsync,
  retransmitirByIdFromFaturaAsync,
  reabrirByIdAsync,
  diagnosticoDiByIdAsync,
  transmissaoDuimpByIdAsync,
  registrarDuimpByIdAsync,
  generateReportDIDiagnosticoAsync,
  generateReportDIRegistroAsync,
  fetchAllDecImportWithPaginationAsync,
  gerarXMLDIagnosticoLoteAsync,
  calcularDisLoteAsync,
  releaseDisEmLoteAsync,
  retransmitirDisEmLoteAsync,
  liberarDisEmLoteAsync,
  fetchMercadoriasByFaturaIdAsync,
  fetchAdicoesByDiDuimpIdAsync,
  fetchAdicoesPaginacaoMetadataByDiDuimpIdAsync,
  fetchCapaDiDuimpByIdFromFaturaAsync,
  updateMercadoriaAsync,
  updateAdicaoAsync,
  transmissaoDiagnosticoDuimpByIdAsync,
  obterPlanilhaFundamentoLegalDuimp,
  obterPlanilhaTratamentoTributarioDuimp,
  importarTratamentosTributariosThunk,
  importarFundamentosLegaisThunk,
  syncAdicoesAsync,
  callSyncAdicoesAPIAsync,
  atualizaDadosCargaAsync,
  obterPlanilhaAtributosItensDuimpAsync,
  importarAtributosItensDuimpAsync,
  fetchPaginatedMercadoriasAsync,
  fetchMercadoriaByIdAsync,
  obterChavesDuimpAsync,
  retificarDuimpAsync,
  apagarTransmissaoDuimpAsync,
};

export {
  activateByIdFromFaturaAsync,
  conferirByIdFromFaturaAsync,
  declaracaoImportacaoThunks,
  diagnosticoDiByIdAsync,
  transmissaoDuimpByIdAsync,
  registrarDuimpByIdAsync,
  duplicarByIdFromFaturaAsync,
  fetchAllFromFaturaAsync,
  fetchByIdFromFaturaAsync,
  generateReportDIDiagnosticoAsync,
  inactivateByIdFromFaturaAsync,
  liberarByIdFromFaturaAsync,
  reabrirByIdAsync,
  retransmitirByIdFromFaturaAsync,
  generateReportDIRegistroAsync,
  fetchAllDecImportWithPaginationAsync,
  gerarXMLDIagnosticoLoteAsync,
  calcularDisLoteAsync,
  releaseDisEmLoteAsync,
  retransmitirDisEmLoteAsync,
  liberarDisEmLoteAsync,
  fetchMercadoriasByFaturaIdAsync,
  fetchAdicoesByDiDuimpIdAsync,
  fetchAdicoesPaginacaoMetadataByDiDuimpIdAsync,
  fetchCapaDiDuimpByIdFromFaturaAsync,
  updateMercadoriaAsync,
  updateAdicaoAsync,
  transmissaoDiagnosticoDuimpByIdAsync,
  obterPlanilhaFundamentoLegalDuimp,
  obterPlanilhaTratamentoTributarioDuimp,
  importarTratamentosTributariosThunk,
  importarFundamentosLegaisThunk,
  syncAdicoesAsync,
  callSyncAdicoesAPIAsync,
  atualizaDadosCargaAsync,
  obterPlanilhaAtributosItensDuimpAsync,
  importarAtributosItensDuimpAsync,
  fetchPaginatedMercadoriasAsync,
  fetchMercadoriaByIdAsync,
  obterChavesDuimpAsync,
  retificarDuimpAsync,
  apagarTransmissaoDuimpAsync,
};
